import React, { useState } from 'react';
import { ArrowRight, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { DEFAULT_CONTENT, SERVICE_OPTIONS, phoneHref } from '../mock';
import { submitEstimate } from '../lib/api';

const emptyForm = { full_name: '', phone: '', email: '', location: '', service: '', details: '' };

const Contact = ({ contact = DEFAULT_CONTENT.contact }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Please enter your full name';
    if (!form.phone.trim()) e.phone = 'Please enter your phone number';
    else if (!/^[0-9+()\-\s]{7,}$/.test(form.phone.trim())) e.phone = 'Enter a valid phone number';
    if (!form.email.trim()) e.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.location.trim()) e.location = 'Please enter the project location';
    if (!form.service) e.service = 'Please select a service';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitEstimate(form);
      toast({ title: 'Estimate request sent!', description: 'Thank you — the Ever Leaf team will get back to you within 24 hours.' });
      setForm(emptyForm);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or call us directly.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (k) => (errors[k] ? <span className="text-[12px] text-red-600 mt-1 block">{errors[k]}</span> : null);
  const areas = contact.areas || [];

  return (
    <section id="contact" className="bg-white">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <span className="text-[12px] font-semibold tracking-[0.25em] text-[#5f9e2c]">FREE ESTIMATE</span>
            <h2 className="font-display text-[30px] md:text-[36px] font-semibold text-[#123020] mt-2">Get a Free Estimate</h2>
            <p className="mt-3 text-[15px] text-[#5a6b60] max-w-[560px]">
              Fill out the form below and we’ll get back to you within 24 hours with a no-obligation quote for your project.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 grid sm:grid-cols-2 gap-5" noValidate>
              <div>
                <Label htmlFor="full_name" className="text-[#123020] font-medium">Full Name</Label>
                <Input id="full_name" value={form.full_name} onChange={(e) => update('full_name', e.target.value)} placeholder="John Doe" className="mt-1.5 h-11 rounded-md border-[#d7d5c8] focus-visible:ring-[#5f9e2c]" />
                {fieldError('full_name')}
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#123020] font-medium">Phone</Label>
                <Input id="phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+1 332 280 2168" className="mt-1.5 h-11 rounded-md border-[#d7d5c8] focus-visible:ring-[#5f9e2c]" />
                {fieldError('phone')}
              </div>
              <div>
                <Label htmlFor="email" className="text-[#123020] font-medium">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" className="mt-1.5 h-11 rounded-md border-[#d7d5c8] focus-visible:ring-[#5f9e2c]" />
                {fieldError('email')}
              </div>
              <div>
                <Label htmlFor="location" className="text-[#123020] font-medium">Project Location</Label>
                <Input id="location" value={form.location} onChange={(e) => update('location', e.target.value)} placeholder="Brooklyn, NY" className="mt-1.5 h-11 rounded-md border-[#d7d5c8] focus-visible:ring-[#5f9e2c]" />
                {fieldError('location')}
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#123020] font-medium">Service Needed</Label>
                <Select value={form.service} onValueChange={(v) => update('service', v)}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-md border-[#d7d5c8] focus:ring-[#5f9e2c]">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
                {fieldError('service')}
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="details" className="text-[#123020] font-medium">Project Details</Label>
                <Textarea id="details" value={form.details} onChange={(e) => update('details', e.target.value)} placeholder="Tell us about your project..." rows={4} className="mt-1.5 rounded-md border-[#d7d5c8] focus-visible:ring-[#5f9e2c] resize-none" />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={submitting} className="bg-[#86c93c] hover:bg-[#75b52f] text-[#123020] font-semibold rounded-md h-12 px-7 text-[15px]">
                  {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>) : (<>Request Free Estimate <ArrowRight className="ml-2 h-4 w-4" /></>)}
                </Button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#123020] rounded-md p-8 h-full text-white">
              <h3 className="font-display text-2xl font-semibold">Contact Information</h3>
              <p className="mt-2 text-[14px] text-[#b9cdb0]">Reach out any time — we’re here to help bring your outdoor space to life.</p>

              <div className="mt-8 space-y-6">
                <a href={`tel:${phoneHref(contact.phone)}`} className="flex items-start gap-4 group">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1b4029] text-[#86c93c] shrink-0"><Phone className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-[12px] text-[#a9c6a0]">Phone</span>
                    <span className="block font-semibold group-hover:text-[#86c93c] transition-colors">{contact.phone}</span>
                  </span>
                </a>
                <a href={`mailto:${contact.email}`} className="flex items-start gap-4 group">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1b4029] text-[#86c93c] shrink-0"><Mail className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-[12px] text-[#a9c6a0]">Email</span>
                    <span className="block font-semibold group-hover:text-[#86c93c] transition-colors break-all">{contact.email}</span>
                  </span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1b4029] text-[#86c93c] shrink-0"><MapPin className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-[12px] text-[#a9c6a0]">Service Areas</span>
                    <span className="block font-semibold">{areas.join(' | ')}</span>
                  </span>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[#2f5a3d]">
                <div className="grid grid-cols-3 gap-3">
                  {areas.map((a) => (
                    <div key={a} className="text-center py-3 rounded-md bg-[#1b4029] text-[13px] font-medium text-[#dce8d6]">{a}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
